import { useState, useCallback, useEffect } from "react";
import { cn } from "utils/helpers";
import SkeletonBase from "newui/components/Skeleton";
import { usePanelMemberStatus } from "../../../../hooks/usePanelScoringQueries";
import type { ScoringMode } from "../../../../hooks/usePanelScoringQueries";
import { useInitialEvaluationContext } from "../../../../hooks/InitialEvaluationContext";
import { PanelMemberList } from "./PanelMemberList";
import { LeadScoringToggle } from "./LeadScoringToggle";
import { LeadScoringSection } from "./LeadScoringSection";
import { MemberScoreAccordion } from "./MemberScoreAccordion";
import { AverageScoringTable } from "./AverageScoringTable";
import { CompanyUserReadOnlyView } from "./CompanyUserReadOnlyView";
import { EvaluationClarificationsTable } from "./clarifications/EvaluationClarificationsTable";
import type { Rfx } from "newui/features/Rfx/types/rfx.types";
import type { EvaluationStepNumber } from "../../../../types/evaluation.type";
import { EVALUATION_STEP_NUMBERS } from "../../../../constants/evaluation.constants";

interface PanelEvaluationViewProps {
	currentStep?: EvaluationStepNumber;
	evalId: number;
	isRfxLead: boolean;
	mode?: ScoringMode;
	rfxData: Rfx;
	onCompletionChange?: (isComplete: boolean) => void;
	onRefreshUserContext?: () => void;
}

type TabId = "my-scoring" | "panel-members" | "my-clarifications";

interface TabConfig {
	id: TabId;
	label: string;
	testId: string;
}

export const PanelEvaluationView: React.FC<PanelEvaluationViewProps> = ({
	rfxData,
	evalId,
	isRfxLead,
	mode = "initial",
	currentStep,
	onCompletionChange,
	onRefreshUserContext,
}) => {
	const rfxId = rfxData.id;
	const { data: members, isLoading, refetch: refetchMembers } = usePanelMemberStatus(rfxId, evalId, mode);
	const { suppliers } = useInitialEvaluationContext();

	const isFinal = mode === "final";
	const activeMembers = (members ?? []).filter((m) => {
		if (m.is_removed) return false;
		if (isFinal && m.status === "declined") return false;
		return true;
	});
	const hasLeadSubmitted = activeMembers.some(
		(m) => m.role === "rfxLead" && m.status === "submitted"
	);

	const [activeTab, setActiveTab] = useState<TabId>(hasLeadSubmitted ? "panel-members" : "my-scoring");
	const [isScoringOverride, setIsScoringOverride] = useState<boolean | null>(null);
	const [clarificationSupplierId, setClarificationSupplierId] = useState<number | null>(null);

	const handleLeadSubmitted = useCallback(() => {
		refetchMembers();
	}, [refetchMembers]);

	const clarificationAvailable = currentStep != null && currentStep <= EVALUATION_STEP_NUMBERS.CLARIFICATIONS;

	const handleAddClarification = useCallback((inviteId: number) => {
		setActiveTab("my-clarifications");
		setClarificationSupplierId(inviteId);
	}, []);

	const handleAutoOpenHandled = useCallback(() => {
		setClarificationSupplierId(null);
	}, []);

	const handleScoringToggle = useCallback((isScoring: boolean) => {
		setIsScoringOverride(isScoring);
		if (!isScoring) {
			setActiveTab("panel-members");
		}
		onRefreshUserContext?.();
	}, [onRefreshUserContext]);

	const allSubmitted = activeMembers.length > 0 &&
		activeMembers.every((m) => {
			if (m.status === "revoked") return true;
			if (isFinal) return m.final_status === "submitted";
			if (m.status === "declined") return true;
			return m.status === "submitted";
		});

	useEffect(() => {
		onCompletionChange?.(allSubmitted);
	}, [allSubmitted, onCompletionChange]);

	if (isLoading) {
		return (
			<div className="space-y-4" data-testid="panel-evaluation-loading">
				{[1, 2, 3].map((i) => (
					<SkeletonBase key={i} startColor="lightgray" width="100%" height="56px" className="!rounded-lg" />
				))}
			</div>
		);
	}

	// Non-lead, non-panel users see a read-only view
	if (!isRfxLead) {
		return (
			<CompanyUserReadOnlyView
				rfxLeadName={rfxData.rfxLead?.fullName ?? "Unknown"}
				rfxLeadEmail={rfxData.rfxLead?.email ?? ""}
				rfxLeadPhone={rfxData.rfxLead?.phoneNumber}
			/>
		);
	}

	const isLeadCurrentlyScoring = isScoringOverride ?? activeMembers.some(
		(m) => m.role === "rfxLead" && !m.is_removed
	);
	const anyMemberSubmitted = activeMembers.some(
		(m) => m.role !== "rfxLead" && m.status === "submitted"
	);
	const leadScoreVisibility = (members ?? [])[0]?.lead_score_visibility ?? "always";

	// In final mode, show panel members content directly (no tabs — parent handles tabs)
	if (mode === "final") {
		return (
			<div className="space-y-6" data-testid="panel-evaluation-view">
				<PanelMemberList rfxId={rfxId} evalId={evalId} mode="final" />

				<MemberScoreAccordion
					rfxId={rfxId}
					evalId={evalId}
					mode="final"
					members={activeMembers}
					isLeadSubmitted={hasLeadSubmitted}
					leadScoreVisibility={leadScoreVisibility}
					isLeadScoring={isLeadCurrentlyScoring}
				/>

				<AverageScoringTable
					rfxId={rfxId}
					evalId={evalId}
					mode="final"
					allSubmitted={allSubmitted}
				/>
			</div>
		);
	}

	const showMyScoring = isLeadCurrentlyScoring;

	const tabs: TabConfig[] = [
		...(showMyScoring ? [{ id: "my-scoring" as TabId, label: "My Scoring", testId: "tab-my-scoring" }] : []),
		{ id: "my-clarifications", label: "My Clarifications", testId: "tab-my-clarifications" },
		{ id: "panel-members", label: "Panel Members", testId: "tab-panel-members" },
	];

	// If active tab is no longer available, switch to first available
	const effectiveTab = tabs.some((t) => t.id === activeTab) ? activeTab : tabs[0].id;

	return (
		<div className="space-y-6" data-testid="panel-evaluation-view">
			{!hasLeadSubmitted && <LeadScoringToggle
				rfxId={rfxId}
				evalId={evalId}
				initialIsScoring={isLeadCurrentlyScoring}
				leadScoreVisibility={leadScoreVisibility}
				anyMemberSubmitted={anyMemberSubmitted}
				onToggle={handleScoringToggle}
			/>}

			<div className="flex border-b border-gray-200" data-testid="panel-tabs" role="tablist">
				{tabs.map((tab) => (
					<button
						key={tab.id}
						type="button"
						role="tab"
						aria-selected={effectiveTab === tab.id}
						aria-controls={`tab-panel-${tab.id}`}
						onClick={() => setActiveTab(tab.id)}
						className={cn(
							"px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
							effectiveTab === tab.id
								? "border-[#0031DD] text-[#0031DD]"
								: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
						)}
						data-testid={tab.testId}
					>
						{tab.label}
					</button>
				))}
			</div>

			{showMyScoring && (
				<div
					id="tab-panel-my-scoring"
					role="tabpanel"
					className={effectiveTab === "my-scoring" ? "space-y-6" : "hidden"}
				>
					<LeadScoringSection
						rfxId={rfxId}
						evalId={evalId}
						isLeadScoring={isLeadCurrentlyScoring}
						isLeadSubmitted={hasLeadSubmitted}
						onAddClarification={clarificationAvailable ? handleAddClarification : undefined}
						onSubmitted={handleLeadSubmitted}
					/>
				</div>
			)}

			<div
				id="tab-panel-panel-members"
				role="tabpanel"
				className={effectiveTab === "panel-members" ? "space-y-6" : "hidden"}
			>
				<PanelMemberList
					rfxId={rfxId}
					evalId={evalId}
					mode={mode}
				/>

				<MemberScoreAccordion
					rfxId={rfxId}
					evalId={evalId}
					mode={mode}
					members={activeMembers}
					isLeadSubmitted={hasLeadSubmitted}
					leadScoreVisibility={leadScoreVisibility}
					isLeadScoring={isLeadCurrentlyScoring}
					onEnterScores={showMyScoring ? () => setActiveTab("my-scoring") : undefined}
					onAddClarification={clarificationAvailable ? handleAddClarification : undefined}
				/>

				<AverageScoringTable
					rfxId={rfxId}
					evalId={evalId}
					mode={mode}
					allSubmitted={allSubmitted}
				/>
			</div>

			<div
				id="tab-panel-my-clarifications"
				role="tabpanel"
				className={effectiveTab === "my-clarifications" ? "space-y-6" : "hidden"}
			>
				{suppliers.length > 0 && (
					<EvaluationClarificationsTable
						rfxId={rfxId}
						evalId={evalId}
						suppliers={suppliers.map((s) => ({
							inviteId: s.inviteId,
							supplierName: s.supplierName,
							supplierLogo: s.supplierLogo,
						}))}
						autoOpenForSupplierId={clarificationSupplierId}
						onAutoOpenHandled={handleAutoOpenHandled}
					/>
				)}
			</div>
		</div>
	);
};
